/**
 * Cart LocalStorage Utilities
 * Manages cart persistence in localStorage
 */

const CART_STORAGE_KEY = "nbyula_cart";

/**
 * A subscription-configured service (has both a razorpayPlanId and a per-cycle
 * amount) is sold monthly, never one-time; mark it so the cart routes it through
 * the subscription checkout at its per-cycle price. Requiring amountPerCycle too
 * avoids mispricing a half-configured service. Plans and non-subscription services
 * pass through untouched. Shared by add-to-cart AND the shared-link restore so
 * both entry points agree.
 */
export function markSubscriptionService(service) {
  const sub = service?.subscription;
  if (service?.isPlan || !sub?.razorpayPlanId || !sub?.amountPerCycle) {
    return service;
  }
  const perCycle = sub.amountPerCycle / 100;
  return {
    ...service,
    isSubscription: true,
    priceInr: perCycle,
    // Only a monthly cycle may claim "/mo", which is how every consumer of this
    // field renders it.
    ...(sub.period && sub.period !== "monthly"
      ? {}
      : { subscriptionPricePerMonth: perCycle }),
  };
}

/**
 * Save cart to localStorage
 */
export function saveCartToStorage(cart) {
  try {
    const cartData = {
      items: cart.items || [],
      total: cart.total || 0,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
  } catch (error) {
    console.error("Error saving cart to localStorage:", error);
  }
}

/**
 * Load cart from localStorage
 */
export function loadCartFromStorage() {
  try {
    const cartData = localStorage.getItem(CART_STORAGE_KEY);
    if (!cartData) {
      return null;
    }

    const parsedCart = JSON.parse(cartData);

    // Optional: Add expiration check (e.g., 7 days)
    const lastUpdated = new Date(parsedCart.lastUpdated);
    const daysSinceUpdate = (new Date() - lastUpdated) / (1000 * 60 * 60 * 24);

    if (daysSinceUpdate > 7) {
      // Cart is too old, clear it
      clearCartStorage();
      return null;
    }

    return {
      items: parsedCart.items || [],
      total: parsedCart.total || 0,
      showCart: false, // Don't auto-show on load
    };
  } catch (error) {
    console.error("Error loading cart from localStorage:", error);
    return null;
  }
}

/**
 * Clear cart from localStorage
 */
export function clearCartStorage() {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing cart from localStorage:", error);
  }
}
